import React, { useState } from 'react';
import { TextVariant } from '@naf/text';
import { TextSnippet } from '@styled-icons/material-outlined/TextSnippet';
import { AdvancedImage } from '@cloudinary/react';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import useSelector from '@redux/typedHooks';
import { cloudinaryAttachment } from '@utils/imageUrl';
import DownloadArrowIcon from '@components/icons/DownloadArrow';
import { formatFileSize } from '@utils/formatFileSize';
import { createNorwegianDateString } from '@utils/createNorwegianDateString';
import { CloudinaryFileType } from '@appTypes/CloudinaryImageType';
import * as S from './DocumentsBlock.styles';

interface Doc {
  documentLink: CloudinaryFileType;
  title: string;
}

interface DocumentsBlockSerializerProps {
  value: {
    documents: Doc[];
  };
}

const Document = ({ doc }: { doc: Doc }) => {
  const application = useSelector((state) => state.application);
  const { getProcessedImage } = useCloudinary();

  const documentId = doc.documentLink.public_id || doc.documentLink.publicId;
  const imgId = doc.documentLink?.public_id || doc.documentLink?.publicId;
  const attachmentUrl = documentId && cloudinaryAttachment(documentId, application, doc.title);
  const [previewImage] = useState((imgId && getProcessedImage(imgId, { width: 32, height: 32 })) || undefined);

  const advancedImage = previewImage ? (
    <AdvancedImage style={{ maxWidth: '100%' }} alt={`${doc.title}`} cldImg={previewImage} plugins={[]} />
  ) : undefined;

  const isDocumentImage =
    doc.documentLink.format &&
    (doc.documentLink.format === 'jpg' || doc.documentLink.format === 'png' || doc.documentLink.format === 'svg');
  const createdDate = doc.documentLink.created_at
    ? createNorwegianDateString(new Date(doc.documentLink.created_at))
    : null;

  if (!doc?.documentLink) return null;

  return (
    <S.ListItem key={doc.documentLink.version}>
      <S.DocumentItemWrapper
        href={attachmentUrl}
        download={attachmentUrl ? `${doc.title}-${doc.documentLink.version}` : undefined}
      >
        <S.DocumentText>
          {isDocumentImage ? advancedImage : <TextSnippet size={32} />}
          <div>
            <S.DocumentHeader variant={TextVariant.BodyTextSubHeader} tag="h5">
              {doc.title || doc.documentLink.public_id}
            </S.DocumentHeader>
            {doc.documentLink.bytes && (
              <S.InfoText variant={TextVariant.Small} tag="span">
                {createdDate} {createdDate && <S.DotIcon />} {formatFileSize(doc.documentLink.bytes)}{' '}
                {doc.documentLink.format && <S.DotIcon />} {doc.documentLink.format?.toUpperCase()}
              </S.InfoText>
            )}
          </div>
        </S.DocumentText>
        <DownloadArrowIcon />
      </S.DocumentItemWrapper>
    </S.ListItem>
  );
};

export const DocumentsBlock = ({ value: { documents } }: DocumentsBlockSerializerProps) => (
  <S.BlockWrapper>{documents?.length > 0 && documents.map((doc) => <Document doc={doc} />)}</S.BlockWrapper>
);
